import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeHtml } from '../utils/sanitize';

const Notepad = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const notepadRef = useRef(null);
  const contentRef = useRef(null);
  const dragHandleRef = useRef(null);
  const resizeHandleRef = useRef(null);
  
  // Position and size state
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState({ width: 400, height: 500 });
  
  // Drag and resize state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  // Content state
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Update contentEditable when content changes externally (e.g., loading from Firestore)
  useEffect(() => {
    if (contentRef.current && contentRef.current.innerHTML !== content && isOpen) {
      // Only update if content actually changed (prevents infinite loop)
      const currentContent = contentRef.current.innerHTML;
      if (currentContent !== content) {
        contentRef.current.innerHTML = sanitizeHtml(content || '');
      }
    }
  }, [isOpen]); // Only run when opening
  
  // Move focus to the content area when the notepad is opened
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setTimeout(() => {
        if (contentRef.current) contentRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  // Load content when opening
  useEffect(() => {
    if (isOpen && user && contentRef.current && !contentRef.current.innerHTML) {
      loadNotepadData();
    }
  }, [isOpen]);
  
  const loadNotepadData = async () => {
    if (!user) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.notepad) {
          const savedContent = sanitizeHtml(userData.notepad.content || '');
          setContent(savedContent);
          if (contentRef.current) {
            contentRef.current.innerHTML = savedContent;
          }
          if (userData.notepad.position) {
            setPosition(userData.notepad.position);
          }
          if (userData.notepad.size) {
            setSize(userData.notepad.size);
          }
        } else {
          // Initialize empty content
          if (contentRef.current) {
            contentRef.current.innerHTML = '';
          }
        }
      }
    } catch (error) {
      console.error('Error loading notepad data:', error);
    }
  };
  
  // Autosave with debounce
  useEffect(() => {
    if (!isOpen || !user) return;
    
    const saveTimeout = setTimeout(() => {
      saveNotepadData();
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(saveTimeout);
  }, [content, position, size, isOpen, user]);
  
  const saveNotepadData = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        notepad: {
          content: sanitizeHtml(content),
          position: position,
          size: size,
          lastUpdated: new Date()
        }
      });
    } catch (error) {
      console.error('Error saving notepad data:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle drag start
  const handleDragStart = (e) => {
    // Only allow dragging from header area, not buttons or content
    if (!e.target.closest('.notepad-header-drag-area') || 
        e.target.closest('button') ||
        e.target.closest('.resize-handle')) {
      return;
    }
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };
  
  // Handle drag
  useEffect(() => {
    if (!isDragging) return;
    
    const handleDrag = (e) => {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };
    
    const handleDragEnd = () => {
      setIsDragging(false);
    };
    
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, dragStart]);
  
  // Handle resize start
  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  };
  
  // Handle resize
  useEffect(() => {
    if (!isResizing) return;
    
    const handleResize = (e) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      setSize({
        width: Math.max(300, resizeStart.width + deltaX),
        height: Math.max(200, resizeStart.height + deltaY)
      });
    };
    
    const handleResizeEnd = () => {
      setIsResizing(false);
    };
    
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', handleResizeEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeStart]);
  
  // Formatting functions
  const formatText = (command, value = null) => {
    if (!contentRef.current) return;
    
    // Ensure the contentEditable is focused
    contentRef.current.focus();
    
    // For list commands, if there's no selection, find the current paragraph/line
    if ((command === 'insertUnorderedList' || command === 'insertOrderedList')) {
      const selection = window.getSelection();
      
      // Only modify selection if there's no current selection
      if (selection.rangeCount === 0 || selection.toString().length === 0) {
        // Get current cursor position - if no range exists, try to preserve cursor
        let range;
        if (selection.rangeCount > 0) {
          range = selection.getRangeAt(0).cloneRange();
        } else {
          // Try to get the actual cursor position by checking if there's a caret
          // If contentEditable is focused, there should be a range
          try {
            // Create a collapsed range at the end (common default position)
            range = document.createRange();
            const walker = document.createTreeWalker(
              contentRef.current,
              NodeFilter.SHOW_TEXT,
              null
            );
            let lastTextNode = null;
            let node;
            while (node = walker.nextNode()) {
              lastTextNode = node;
            }
            if (lastTextNode) {
              range.setStart(lastTextNode, lastTextNode.textContent.length);
              range.collapse(true);
            } else {
              range.setStart(contentRef.current, contentRef.current.childNodes.length);
              range.collapse(true);
            }
          } catch (e) {
            range = document.createRange();
            range.setStart(contentRef.current, 0);
            range.collapse(true);
          }
        }
        
        // Find the current block element (paragraph/div) containing cursor
        let node = range.commonAncestorContainer;
        
        // Walk up the DOM to find a block element
        while (node && node !== contentRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName;
            // If already in a list item, just use that
            if (tagName === 'LI') {
              const newRange = document.createRange();
              newRange.selectNodeContents(node);
              selection.removeAllRanges();
              selection.addRange(newRange);
              break;
            }
            // If in a paragraph or div, select just that block
            if (tagName === 'P' || tagName === 'DIV') {
              const newRange = document.createRange();
              newRange.selectNodeContents(node);
              selection.removeAllRanges();
              selection.addRange(newRange);
              break;
            }
          }
          node = node.parentElement;
        }
        
        // If no block element found and we have plain text, find current line
        if ((!node || node === contentRef.current) && range.startContainer) {
          const textContainer = range.startContainer.nodeType === Node.TEXT_NODE
            ? range.startContainer
            : (range.startContainer.firstChild || range.startContainer);
          
          if (textContainer && textContainer.nodeType === Node.TEXT_NODE) {
            const text = textContainer.textContent || '';
            const offset = range.startOffset;
            
            // Find line boundaries
            let lineStart = 0;
            let lineEnd = text.length;
            
            // Search backwards for newline
            for (let i = offset - 1; i >= 0; i--) {
              if (text[i] === '\n') {
                lineStart = i + 1;
                break;
              }
            }
            
            // Search forwards for newline
            for (let i = offset; i < text.length; i++) {
              if (text[i] === '\n') {
                lineEnd = i;
                break;
              }
            }
            
            // Select just this line
            const newRange = document.createRange();
            newRange.setStart(textContainer, lineStart);
            newRange.setEnd(textContainer, lineEnd);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      }
    }
    
    // Execute the command
    try {
      const success = document.execCommand(command, false, value);
      if (!success) {
        console.warn(`Failed to execute command: ${command}`);
      }
    } catch (error) {
      console.error(`Error executing command ${command}:`, error);
    }
    
    // Update content state
    if (contentRef.current) {
      setContent(sanitizeHtml(contentRef.current.innerHTML));
    }
    
    // Keep focus after command
    contentRef.current.focus();
  };
  
  // Handle content change
  const handleContentChange = () => {
    if (contentRef.current) {
      setContent(sanitizeHtml(contentRef.current.innerHTML));
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div
      ref={notepadRef}
      role="dialog"
      aria-modal="false"
      aria-label="Notes"
      className="fixed z-[100] shadow-2xl rounded-lg overflow-hidden"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        backgroundColor: '#fef9c3', // Sticky note yellow
      }}
    >
      {/* Header/Toolbar */}
      <div 
        className="notepad-toolbar notepad-header-drag-area bg-yellow-200 border-b border-yellow-300 px-3 py-2 flex items-center justify-between" 
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }} 
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center space-x-2">
          {/* Formatting buttons */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              formatText('bold');
            }}
            className="px-2 py-1 hover:bg-yellow-300 rounded transition-colors font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Bold"
          >
            B
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              formatText('italic');
            }}
            className="px-2 py-1 hover:bg-yellow-300 rounded transition-colors italic text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Italic"
          >
            I
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              formatText('underline');
            }}
            className="px-2 py-1 hover:bg-yellow-300 rounded transition-colors underline text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Underline"
          >
            U
          </button>
          <div aria-hidden="true" className="w-px h-6 bg-yellow-400 mx-1" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              formatText('insertUnorderedList');
            }}
            className="px-2 py-1 hover:bg-yellow-300 rounded transition-colors text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Bullet list"
          >
            <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              formatText('insertOrderedList');
            }}
            className="px-2 py-1 hover:bg-yellow-300 rounded transition-colors text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Numbered list"
          >
            <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
          </button>
        </div>
        <div className="flex items-center space-x-2">
          {/* role=status announces auto-save completion to screen readers (WCAG 4.1.3 Status Messages) */}
          <span role="status" aria-live="polite" className="text-xs text-gray-700">
            {isSaving ? 'Saving...' : ''}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 hover:bg-yellow-300 rounded transition-colors text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Close notepad"
          >
            <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Content area — role="textbox" exposes the editable region semantics to AT */}
      <div
        ref={contentRef}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Notes text area"
        className="p-4 h-full overflow-y-auto focus:outline-none font-inter text-gray-900"
        style={{
          minHeight: `${size.height - 60}px`,
          maxHeight: `${size.height - 60}px`,
          userSelect: 'text',
          color: '#1f2937' // Dark gray for good contrast on yellow
        }}
        onInput={handleContentChange}
        onMouseDown={(e) => e.stopPropagation()}
        suppressContentEditableWarning={true}
        data-placeholder="Add here ..."
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          /* #4b5563 (gray-600) yields ~5.9:1 contrast on yellow-100 (#fef9c3), meeting WCAG 1.4.3 */
          color: #4b5563;
          pointer-events: none;
        }
        [contenteditable] {
          color: #1f2937;
        }
        [contenteditable] * {
          color: inherit;
        }
        [contenteditable] ul, [contenteditable] ol {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }
        [contenteditable] ul {
          list-style-type: disc;
        }
        [contenteditable] ol {
          list-style-type: decimal;
        }
        [contenteditable] li {
          margin: 0.25rem 0;
          display: list-item;
        }
      `}</style>
      
      {/* Resize handle */}
      <div
        ref={resizeHandleRef}
        className="resize-handle absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize"
        onMouseDown={handleResizeStart}
        style={{
          background: 'linear-gradient(135deg, transparent 0%, transparent 30%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.1) 100%)'
        }}
      />
    </div>
  );
};

export default Notepad;

